/**
 * Transition Renderer - Animated transitions between steps
 */

import React, { useEffect } from 'react';
import { TransitionConfig, FlowContext } from '../core/types';
import { motion } from 'motion/react';

export interface TransitionRendererProps {
  config: TransitionConfig;
  context: FlowContext;
  onComplete?: () => void;
  autoAdvance?: boolean;
  autoAdvanceDelay?: number;
  autoProgressEnabled?: boolean;
}

export const TransitionRenderer: React.FC<TransitionRendererProps> = ({
  config,
  context,
  onComplete,
  autoAdvance = true,
  autoAdvanceDelay = 600,
  autoProgressEnabled = false,
}) => {
  // Get transition animation variants
  const getTransitionVariants = () => {
    switch (config.type) {
      case 'fade':
        return {
          initial: { opacity: 0 },
          animate: { opacity: 1 },
          exit: { opacity: 0 },
        };

      case 'slide':
        return {
          initial: { x: '100%', opacity: 0 },
          animate: { x: 0, opacity: 1 },
          exit: { x: '-100%', opacity: 0 },
        };

      case 'zoom':
        return {
          initial: { scale: 0.8, opacity: 0 },
          animate: { scale: 1, opacity: 1 },
          exit: { scale: 1.2, opacity: 0 },
        };

      case 'blur':
        return {
          initial: { filter: 'blur(10px)', opacity: 0 },
          animate: { filter: 'blur(0px)', opacity: 1 },
          exit: { filter: 'blur(10px)', opacity: 0 },
        };

      default:
        return {
          initial: { opacity: 0 },
          animate: { opacity: 1 },
          exit: { opacity: 0 },
        };
    }
  };

  const variants = getTransitionVariants();
  const duration = (config.duration || 600) / 1000;

  // Auto-advance after delay (only if toggle is enabled)
  useEffect(() => {
    if (autoAdvance && onComplete && autoProgressEnabled) {
      const timer = setTimeout(() => {
        onComplete();
      }, autoAdvanceDelay);

      return () => clearTimeout(timer);
    }
  }, [autoAdvance, autoAdvanceDelay, autoProgressEnabled, onComplete]);

  return (
    <motion.div
      className="flex items-center justify-center h-full"
      initial={variants.initial}
      animate={variants.animate}
      exit={variants.exit}
      transition={{ duration }}
    >
      <div className="text-center">
        {/* Loading spinner */}
        <motion.div
          className="w-16 h-16 mx-auto mb-6"
          animate={{ rotate: 360 }}
          transition={{
            repeat: Infinity,
            duration: 1,
            ease: 'linear',
          }}
        >
          <div className="w-full h-full border-4 border-primary/20 border-t-primary rounded-full" />
        </motion.div>

        {/* Message */}
        {config.message && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-white/90 text-lg"
          >
            {config.message}
          </motion.div>
        )}

        {/* Animated dots */}
        <motion.div className="flex items-center justify-center gap-2 mt-4">
          {[0, 1, 2].map((i) => (
            <motion.div
              key={i}
              className="w-2 h-2 rounded-full bg-primary"
              animate={{
                opacity: [0.3, 1, 0.3],
                scale: [1, 1.2, 1],
              }}
              transition={{
                repeat: Infinity,
                duration: 1.5,
                delay: i * 0.2,
              }}
            />
          ))}
        </motion.div>
      </div>
    </motion.div>
  );
};
